import { ODataModel } from '../../interfaces/odata-model.interface';
import { JsonUtilities } from '../../utilities/json-utilities';

export class TreeViewNode<T extends ODataModel> {
    public hasChildren = false;
    public isExpanded = false;
    public constructor(
        public id: number,
        public value: string, // the title of the node
        public payload: T, // the actual payload, ie. the ODataModel
        public parent?: TreeViewNode<T>,
        public selected = false,
        public children: Array<TreeViewNode<T>> = [],
        public selectedChildren: number[] = [],
    ) {
        // the id cannot be 0 due to a bug in ng2-tree
        if (id === 0) {
            throw new Error(`TreeViewNode id cannot be 0 for node '${value}')`);
        }

        this.hasChildren = children.length > 0;
    }

    public collapse(): void {
        this.isExpanded = false;
    }

    public expand(): void {
        this.isExpanded = true;
    }

    /**
     * Since the TreeViewNode contains circular references between parent and child nodes, we need to
     * take care of this circular reference before converting a node to JSON.
     * We do this by providing a custom toJSON implementation that deletes the parent property; upon recreation out of
     * JSON the parent relationship will be restored by the TreeViewModel.
     *
     * @returns {Partial<TreeViewNode<T>>} An object containing selected properties of 'this'
     */
    public toJSON(): Partial<TreeViewNode<T>> {
        return {
            id: this.id,
            value: this.value,
            payload: this.payload,
            selected: this.selected,
            children: this.children,
            selectedChildren: this.selectedChildren,
        };
    }
}

export class TreeViewNodeFromJson<T extends ODataModel> extends TreeViewNode<T> {
    public constructor(type: new (...args: unknown[]) => T, data: JsonTreeViewNode) {
        super(
            data.id,
            data.value,
            JsonUtilities.fromJson<T>(type, data.payload),
            undefined,
            data.selected,
            data.nodes.map((node) => {
                return new TreeViewNodeFromJson<T>(type, node);
            }),
            data.selectedChildren,
        );
    }
}

export interface JsonTreeViewNode {
    id: number;
    value: string;
    payload: unknown;
    selected: boolean;
    nodes: JsonTreeViewNode[];
    selectedChildren: number[];
}
